<template>
  <v-breadcrumbs
    v-if="breadcrumbItems.length > 0"
    :items="breadcrumbItems"
    class="px-0 py-2"
    density="compact"
  >
    <template v-slot:item="{ item }">
      <v-breadcrumbs-item
        v-if="!item.selector"
        :href="item.href"
        :disabled="item.disabled"
        @click="item.click"
        class="text-body-2"
      >
        {{ item.title }}
      </v-breadcrumbs-item>
      
      <!-- List selector breadcrumb item -->
      <v-menu v-else bottom offset-y>
        <template v-slot:activator="{ props }">
          <v-breadcrumbs-item
            v-bind="props"
            :disabled="item.disabled"
            class="text-body-2 cursor-pointer"
            style="cursor: pointer;"
          >
            {{ item.title }}
            <v-icon size="small" class="ml-1">mdi-chevron-down</v-icon>
          </v-breadcrumbs-item>
        </template>
        
        <v-card max-width="400" elevation="8">
          <v-card-title class="text-subtitle-1 py-2">
            <v-icon class="mr-2">mdi-format-list-bulleted</v-icon>
            Switch to List
          </v-card-title>
          <v-divider></v-divider>
          <v-list density="compact" max-height="300" class="overflow-y-auto">
            <template v-if="availablePacklists.length > 0">
              <v-list-item
                v-for="list in availablePacklists"
                :key="list.id"
                @click="selectPacklist(list)"
                :class="{ 'v-list-item--active': list.id === currentListId }"
              >
                <v-list-item-title class="text-body-2">
                  {{ getPacklistDisplayName(list) }}
                </v-list-item-title>
                <v-list-item-subtitle v-if="list.description" class="text-caption">
                  {{ list.description }}
                </v-list-item-subtitle>
              </v-list-item>
              <v-divider class="my-1"></v-divider>
            </template>
            <template v-else>
              <v-list-item disabled>
                <v-list-item-title class="text-body-2 text-grey">
                  No lists available
                </v-list-item-title>
              </v-list-item>
              <v-divider class="my-1"></v-divider>
            </template>
            <v-list-item @click="goToPacklists" prepend-icon="mdi-view-list">
              <v-list-item-title class="text-body-2">View All Lists</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-card>
      </v-menu>
    </template>
    
    <template v-slot:divider>
      <v-icon size="small">mdi-chevron-right</v-icon>
    </template>
  </v-breadcrumbs>
</template>

<script>
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useListStore } from '../stores/list'

export default {
  name: 'BreadcrumbNav',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const listStore = useListStore()

    const currentListId = computed(() => {
      if (route.name === 'ListDetailView') {
        return route.params.uuid || listStore.currentList?.id
      }
      return null
    })

    const currentList = computed(() => {
      if (currentListId.value) {
        return listStore.lists?.find(p => p.id.toString() === currentListId.value.toString()) ||
               listStore.currentList
      }
      return null
    })

    const availablePacklists = computed(() => {
      return listStore.lists || []
    })

    const getPacklistDisplayName = (list) => {
      if (!list) return ''
      return list.name
    }

    const breadcrumbItems = computed(() => {
      const items = []

      // Don't show breadcrumbs on login/register/setup pages
      if (['Login', 'Register', 'Setup'].includes(route.name)) {
        return items
      }

      // Always add "Packlists" as the root for authenticated routes
      if (route.name !== 'ListView') {
        items.push({
          title: 'Lists',
          href: '/lists',
          click: () => router.push('/lists'),
          disabled: false
        })
      }

      // Add current list if we're viewing one
      if (route.name === 'ListDetailView' && currentList.value) {
        items.push({
          title: getPacklistDisplayName(currentList.value),
          selector: true,
          disabled: false
        })
      }

      // Add other route-specific breadcrumbs
      if (route.name === 'SharesView') {
        items.push({
          title: 'Shared Links',
          disabled: true
        })
      } else if (route.name === 'Profile') {
        items.push({
          title: 'Profile',
          disabled: true
        })
      } else if (route.name === 'Admin') {
        items.push({
          title: 'Admin Panel',
          disabled: true
        })
      }

      return items
    })

    const selectPacklist = (list) => {
      if (list.id !== currentListId.value) {
        router.push(`/lists/${list.id}`)
      }
    }

    const goToPacklists = () => {
      router.push('/lists')
    }

    return {
      breadcrumbItems,
      availablePacklists,
      currentListId,
      getPacklistDisplayName,
      selectPacklist,
      goToPacklists
    }
  }
}
</script>

<style scoped>
.v-breadcrumbs {
  padding: 8px 0;
  background: rgba(0, 0, 0, 0.02);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  margin-bottom: 4px;
}

.v-breadcrumbs-item {
  font-size: 0.875rem;
}

.cursor-pointer {
  cursor: pointer;
}

/* Make the dropdown arrow more subtle */
.v-breadcrumbs-item .v-icon {
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.v-breadcrumbs-item:hover .v-icon {
  opacity: 1;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .v-breadcrumbs-item {
    font-size: 0.75rem;
  }
  
  /* Truncate long list names on mobile */
  .v-breadcrumbs-item {
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

@media (max-width: 480px) {
  .v-breadcrumbs-item {
    font-size: 0.7rem;
    max-width: 150px;
  }
}
</style>